import { useState } from 'react';
import { Bell, Check, ExternalLink } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { NotificationItem } from './NotificationItem';
import {
  useNotifications,
  useUnreadNotificationCount,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useAppBadgeSync,
} from '../../hooks/useNotifications';
import type { NotificationResponse } from '../../types/notifications';
import { cn } from '../../lib/utils';

/**
 * Notification bell with dropdown showing recent notifications
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Fetch unread count for badge
  const { data: countData } = useUnreadNotificationCount();
  const unreadCount = countData?.unreadCount ?? 0;

  // Sync PWA app badge with unread count (iOS 16.4+, Chrome/Edge desktop)
  useAppBadgeSync();

  // Fetch recent notifications for dropdown
  const { data: notificationsData, isLoading } = useNotifications(
    { pageSize: 5, page: 1 },
    open // Only fetch when dropdown is open
  );
  const notifications = notificationsData?.notifications ?? [];

  // Mutations
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const handleNotificationClick = (notification: NotificationResponse) => {
    setOpen(false);
    // Navigate to the action URL if available
    if (notification.actionUrl) {
      // If it's a relative URL, navigate with router
      if (notification.actionUrl.startsWith('/')) {
        navigate({ to: notification.actionUrl });
      } else {
        // For absolute URLs, extract path and navigate if it's the same origin
        try {
          const url = new URL(notification.actionUrl);
          if (url.origin === window.location.origin) {
            // Same origin - navigate within app
            navigate({ to: url.pathname + url.search + url.hash });
          } else {
            // Different origin - open in new tab
            window.open(notification.actionUrl, '_blank');
          }
        } catch {
          // Invalid URL - try navigating as-is
          navigate({ to: notification.actionUrl });
        }
      }
    }
  };

  const handleMarkAsRead = (id: string) => {
    markAsRead.mutate(id);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate({ to: '/helpdesk/notifications' });
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              className={cn(
                'absolute -top-1 -right-1 flex items-center justify-center',
                'min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-xs font-medium px-1'
              )}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        collisionPadding={16}
        className="w-80 max-w-[calc(100vw-2rem)]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 gap-2">
          <h4 className="text-sm font-semibold">Notifications</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto py-1 px-2 text-xs shrink-0"
              onClick={handleMarkAllAsRead}
              disabled={markAllAsRead.isPending}
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <Separator />

        {/* Notifications list */}
        <ScrollArea className="max-h-[300px]">
          {isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Loading notifications...
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No notifications yet
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={handleNotificationClick}
                  onMarkAsRead={handleMarkAsRead}
                  compact
                />
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <Separator />
        <div className="p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center text-xs"
            onClick={handleViewAll}
          >
            View all notifications
            <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
